import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useRoom } from "./RoomProvider";
import { apiClient } from "../../lib/api";

/**
 * CommandLine — press `/` to summon. Type or speak. The verb surface.
 *
 * This replaces the sidebar, the "new trip" button, and 80% of the chrome.
 * Verbs like: new trip, call marcus, show chicago, broadcast,
 * reassign 440 to sarah, provider motor city, rider barnetta, help.
 *
 * Fixes VA Walkthrough Finding 1 (no way to find a trip) — any query that
 * isn't a recognized verb runs the same global search VA/admin already use
 * elsewhere (`GET /api/admin/search`), across trip id, rider name/phone,
 * driver, TP. Fixes Finding 2 (results vanish in ~900ms) — the panel no
 * longer force-closes after every command; it stays open until the VA
 * presses Esc or clicks away, same as everything else on the Floor.
 */
export default function CommandLine() {
  const { showCommand, setShowCommand, network, focusOn, cameraTo,
          identity, sosTripId, liveDrivers, allDrivers, realProviders, callSession,
          setDialerOpen } = useRoom();
  const [value, setValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [lastResult, setLastResult] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (showCommand) {
      setValue("");
      setLastResult(null);
      setSearchResults(null);
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [showCommand]);

  // ─── autocomplete verbs + names ──────────────────────────────────
  useEffect(() => {
    if (!value.trim()) { setSuggestions([]); return; }
    const q = value.toLowerCase();
    const out = [];

    const verbs = [
      "new trip", "new cpd trip", "call ", "broadcast ", "find ", "trip ",
      "show chicago", "show houston", "show world", "provider ", "driver ",
    ];
    verbs.forEach(v => { if (v.startsWith(q) || q.startsWith(v)) out.push({ kind: "verb", label: v }); });

    (liveDrivers || []).forEach(d => {
      if ((d.name || "").toLowerCase().includes(q)) {
        out.push({ kind: "driver", id: d.driver_id, label: `Driver · ${d.name}` });
      }
    });
    (realProviders || []).forEach(p => {
      if ((p.name || "").toLowerCase().includes(q)) {
        out.push({ kind: "provider", id: p.tp_id, label: `Provider · ${p.name}` });
      }
    });
    setSuggestions(out.slice(0, 6));
  }, [value, network, liveDrivers, realProviders]);

  const runSearch = async (query) => {
    setSearching(true);
    setLastResult(null);
    try {
      const { data } = await apiClient.get("/admin/search", { params: { q: query } });
      const results = data?.data?.results || [];
      setSearchResults({ query, results });
    } catch (e) {
      setSearchResults({ query, results: [], error: e?.response?.data?.error?.message || "search failed" });
    } finally {
      setSearching(false);
    }
  };

  const execute = (raw) => {
    const q = raw.trim().toLowerCase();
    if (!q) return;
    setSearchResults(null);

    // camera
    if (q === "show world" || q === "world")     { cameraTo("world");   setLastResult("camera → world"); }
    else if (q.includes("chicago"))              { cameraTo("chicago"); setLastResult("camera → Chicago"); }
    else if (q.includes("houston"))              { cameraTo("houston"); setLastResult("camera → Houston"); }

    // new trip
    else if (q.startsWith("new cpd trip") || q === "cpd trip" || q === "new cpd")
      { focusOn("composeCpd", null); setLastResult("compose · new CPD trip"); }
    else if (q.startsWith("new trip") || q === "new")
      { focusOn("compose", null); setLastResult("compose · new trip"); }

    // call a driver — real on-shift driver (by name, GPS freshness doesn't
    // matter for placing a call), real breakout room (SignalWire), same
    // infra a trip's "call driver" button uses.
    else if (q.startsWith("call ")) {
      const who = q.slice(5);
      const roster = (allDrivers && allDrivers.length ? allDrivers : liveDrivers) || [];
      const d = roster.find(x => (x.name || "").toLowerCase().includes(who));
      if (d) {
        setLastResult(`ringing ${d.name} through MedexOVA bot line…`);
        callSession?.start({
          targets: [{ kind: "driver", id: d.driver_id, name: d.name }],
          purpose: "call_driver",
        });
      } else setLastResult(`i couldn't find "${who}" on shift · try "dial" to open the dialer and call any number`);
    }

    // open the general-purpose dialer (any driver, any raw phone number)
    else if (q === "dial" || q === "dialer" || q.startsWith("dial ")) {
      setDialerOpen(true);
      setLastResult("dialer →");
    }

    // broadcast
    else if (q.startsWith("broadcast")) {
      setLastResult("broadcast composer · workspace →");
      focusOn("broadcast", null);
    }

    // direct entity focus — real providers + real live drivers
    else if (q.startsWith("provider ")) {
      const nm = q.slice(9);
      const p = (realProviders || []).find(x => (x.name || "").toLowerCase().includes(nm));
      if (p) { focusOn("provider", p.tp_id); setLastResult(`workspace → ${p.name}`); }
      else setLastResult(`no provider matching "${nm}"`);
    }
    else if (q.startsWith("driver ")) {
      const nm = q.slice(7);
      const d = (liveDrivers || []).find(x => (x.name || "").toLowerCase().includes(nm));
      if (d) { focusOn("liveDriver", d.driver_id); setLastResult(`workspace → ${d.name}`); }
      else setLastResult(`no driver currently on shift matching "${nm}"`);
    }

    // SOS — informational only. Real SOS is detected automatically from
    // a rider's panic tap (trip.sos_flag) — there's no manual trigger.
    else if (q === "sos") {
      setLastResult(sosTripId
        ? `SOS is already active · trip ${String(sosTripId).slice(0, 8)} · the room is on this`
        : `no SOS active · this fires automatically the moment a rider taps panic`);
    }

    // Pin / unpin the currently focused real trip to the tray
    else if (q === "pin" || q === "unpin") {
      setLastResult(`open a real trip on the map, then press P to pin it`);
    }

    // Explicit search verbs
    else if (q.startsWith("find ") || q.startsWith("trip ")) {
      runSearch(raw.trim().slice(5));
    }

    // help / me
    else if (q === "help") {
      const name = identity?.coworker_name || "Nova";
      setLastResult(`${name}: try — new trip · new cpd trip · find [rider/trip id/driver] · call marcus · show chicago · broadcast`);
    }

    // Fallback — this used to be "i'm not sure yet." Instead, actually
    // search for it: trip id, rider name/phone, driver, TP, org.
    else {
      runSearch(raw.trim());
    }

    setValue("");
    // Navigational commands (camera/compose/call/broadcast/provider/driver)
    // close on their own after registering — informational results and
    // search results stay open until the VA dismisses them (Esc / click
    // away). No more fire-and-fade.
  };

  const takeSuggestion = (s) => {
    if (s.kind === "driver")   { focusOn("liveDriver", s.id);   setShowCommand(false); }
    else if (s.kind === "provider") { focusOn("provider", s.id); setShowCommand(false); }
    else { setValue(s.label); }
  };

  const takeSearchResult = (r) => {
    if (r.kind === "trip" && r.meta?.trip_id) {
      focusOn("realTrip", r.meta.trip_id);
      setShowCommand(false);
    }
    // rider/driver/tp/org results don't yet have a Floor workspace view —
    // keep them visible (title/subtitle already show the answer) rather
    // than pretend to navigate somewhere that doesn't exist.
  };

  return (
    <AnimatePresence>
      {showCommand && (
        <motion.div className="cmd-scrim"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }} onClick={() => setShowCommand(false)}>
          <motion.div className="cmd-panel" onClick={e => e.stopPropagation()}
            initial={{ y: -30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            exit={{ y: -20, opacity: 0 }} transition={{ duration: 0.22 }}>
            <div className="cmd-prompt">›</div>
            <input
              ref={inputRef}
              className="cmd-input"
              value={value}
              placeholder="tell the room what to do · or just type a name, trip id, or phone"
              onChange={e => setValue(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") execute(value); }}
              data-testid="floor-command-input"
            />
            <div className="cmd-hint">enter · esc</div>

            {suggestions.length > 0 && (
              <div className="cmd-suggestions">
                {suggestions.map((s, i) => (
                  <div key={i} className="cmd-sug" onClick={() => takeSuggestion(s)}>
                    <span className="cmd-sug-kind">{s.kind}</span>
                    <span className="cmd-sug-label">{s.label}</span>
                  </div>
                ))}
              </div>
            )}

            {lastResult && (
              <div className="cmd-result" data-testid="floor-command-result">{lastResult}</div>
            )}

            {searching && (
              <div className="cmd-result" data-testid="floor-command-searching">searching…</div>
            )}

            {searchResults && !searching && (
              <div className="cmd-search-results" data-testid="floor-command-search-results">
                <div className="cmd-search-head">
                  {searchResults.results.length > 0
                    ? `${searchResults.results.length} match${searchResults.results.length === 1 ? "" : "es"} for "${searchResults.query}"`
                    : searchResults.error
                      ? `couldn't search · ${searchResults.error}`
                      : `nothing found for "${searchResults.query}" · try a trip id, rider name, or phone number`}
                </div>
                {searchResults.results.map((r, i) => (
                  <div key={i}
                       className={`cmd-search-row ${r.kind === "trip" ? "cmd-search-row--actionable" : ""}`}
                       onClick={() => takeSearchResult(r)}
                       data-testid={`floor-command-result-${i}`}>
                    <span className="cmd-search-kind">{r.kind}</span>
                    <div className="cmd-search-body">
                      <div className="cmd-search-title">{r.title}</div>
                      <div className="cmd-search-sub">{r.subtitle}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
